// ============================================================================
// USER MANAGEMENT SERVICE - frontend/src/services/userManagement.ts
// API calls for user management (system admin only)
// ============================================================================

import { api } from './api';

export interface UserProfile {
  userId: string;
  cognitoSub: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  organizationId: string;
  roleAssignments?: Array<{ roleId: string }>;
  status: 'active' | 'pending' | 'suspended' | 'deleted';
  isOffboarded?: boolean;
  provider?: 'cognito' | 'entra';
  entraLinked?: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  lastLoginAt?: string;
  mcpEnabled?: boolean;
  mcpTokenHint?: string;
  mcpWriteEnabled?: boolean;
}

export interface CreateUserInput {
  email: string;
  firstName: string;
  lastName: string;
  organizationId: string;
  roleIds: string[];
  temporaryPassword?: string;
}

export interface UpdateUserInput {
  firstName?: string;
  lastName?: string;
  organizationId?: string;
  status?: 'active' | 'suspended';
}

/**
 * List all users with optional filters
 */
export async function listUsers(filters?: {
  organizationId?: string;
  roleId?: string;
  status?: string;
  search?: string;
}): Promise<{ users: UserProfile[]; total: number }> {
  const params = new URLSearchParams();
  if (filters?.organizationId) params.append('organizationId', filters.organizationId);
  if (filters?.roleId) params.append('roleId', filters.roleId);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.search) params.append('search', filters.search);

  const response = await api.get(`/api/admin/users?${params.toString()}`);
  return response.data;
}

/**
 * Get user details by ID
 */
export async function getUser(userId: string): Promise<UserProfile> {
  const response = await api.get(`/api/admin/users/${userId}`);
  return response.data.user;
}

/**
 * Create a new user
 */
export async function createUser(input: CreateUserInput): Promise<{ user: UserProfile; temporaryPassword: string }> {
  const response = await api.post('/api/admin/users', input);
  return { user: response.data.user, temporaryPassword: response.data.temporaryPassword };
}

/**
 * Update user details
 */
export async function updateUser(userId: string, updates: UpdateUserInput): Promise<UserProfile> {
  const response = await api.put(`/api/admin/users/${userId}`, updates);
  return response.data.user;
}

/**
 * Suspend user account
 */
/**
 * Suspend user account. `partial` means the status stuck (platform and MCP
 * are blocked) but sign-in could not be disabled.
 */
export async function suspendUser(userId: string, reason?: string): Promise<{ partial: boolean; message: string }> {
  const response = await api.post(`/api/admin/users/${userId}/suspend`, { reason });
  return { partial: !!response.data.partial, message: response.data.message };
}

/**
 * Activate user account
 */
export async function activateUser(userId: string): Promise<void> {
  await api.post(`/api/admin/users/${userId}/activate`);
}

/**
 * Delete user (soft delete)
 */
export async function deleteUser(userId: string): Promise<void> {
  await api.delete(`/api/admin/users/${userId}`);
}

/**
 * Reset user password
 */
export async function resetUserPassword(userId: string): Promise<{ temporaryPassword: string }> {
  const response = await api.post(`/api/admin/users/${userId}/reset-password`);
  return response.data;
}

/**
 * Disable a user's personal calendar MCP access (admin override — no self-service token needed)
 */
export async function disableUserMcpAdmin(userId: string): Promise<void> {
  await api.post(`/api/admin/users/${userId}/mcp/disable`);
}

// ============================================================================
// OFFBOARDING — repeatable "user is leaving" system admin action
// ============================================================================

export interface AffectedProject {
  projectId: string;
  name: string;
}

export interface OwnedProject extends AffectedProject {
  status?: string;
}

export interface OffboardPreview {
  projects: AffectedProject[];
  ownedProjects: OwnedProject[];
}

export interface OffboardResult {
  cognitoSuspended: boolean;
  globalSignOut: boolean;
  statusUpdated: boolean;
  personalTokenRotated: boolean;
  projectTokensRevoked: boolean;
  projects: AffectedProject[];
  ownedProjects: OwnedProject[];
  partial: boolean;
}

/**
 * Lists the projects where this user's own MCP tokens will be revoked if they
 * are offboarded, and the projects they own that need transferring.
 */
export async function getOffboardPreview(userId: string): Promise<OffboardPreview> {
  const response = await api.get(`/api/admin/users/${userId}/offboard-preview`);
  return response.data;
}

/**
 * Offboards a leaver: suspends + force signs out the account, greys
 * facilitators out of the studio-booking swim lane, disables their personal
 * calendar MCP token and revokes every project MCP token they hold.
 */
export async function offboardUser(userId: string): Promise<OffboardResult> {
  const response = await api.post(`/api/admin/users/${userId}/offboard`);
  return response.data.result;
}

/**
 * Reinstates a previously offboarded user.
 */
export async function reinstateUser(userId: string): Promise<void> {
  await api.post(`/api/admin/users/${userId}/reinstate`);
}
